import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface OptionCard<T extends string> {
  value: T;
  label: string;
  description?: ReactNode;
  icon?: ReactNode;
}

/** Grupo de opciones tipo radio, grandes para tocar con el dedo. */
export function OptionCards<T extends string>({
  value,
  onChange,
  options,
  name,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: OptionCard<T>[];
  name: string;
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className={cn('grid gap-2 sm:grid-cols-3', className)}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
              selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-accent',
            )}
          >
            {o.icon && <span className={cn('mt-0.5 [&_svg]:size-5', selected ? 'text-primary' : 'text-muted-foreground')}>{o.icon}</span>}
            <span className="grid gap-0.5">
              <span className="text-sm font-medium">{o.label}</span>
              {o.description && <span className="text-xs text-muted-foreground">{o.description}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Chips seleccionables (un solo valor). */
export function ChipGroup<T extends string | number>({
  value,
  onChange,
  options,
  name,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
  name: string;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'h-9 rounded-full border px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
              selected ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
